import { useUser } from "@clerk/clerk-react";
import { Edit3 } from "lucide-react";
import { useState, useMemo } from "react";
import ActionBar from "../components/actionBar/ActionBar";
import EditUserModal from "../components/modals/userModal/EditUserModal";
import UserModal from "../components/modals/userModal/UserModal";
import { useUsers } from "../hooks/useUsers";
import strings from "../i18n/i18nStrings";
import { TUser } from "../types/Users";
import { isAdmin } from "../utils/users";
import { toast } from "react-toastify";

// const MAX_USERS = 5;

const UsersPage = () => {
  const { users, updateUser, mutate } = useUsers();
  const { user: loggedInUser } = useUser();
  
  // Sort users to put logged-in user first
  const sortedUsers = useMemo(() => {
    if (!users || !loggedInUser) return users;
    
    const loggedInUserId = loggedInUser.publicMetadata["external_id"];
    const currentUser = users.find(u => u._id === loggedInUserId);
    const otherUsers = users.filter(u => u._id !== loggedInUserId);
    
    return currentUser ? [currentUser, ...otherUsers] : users;
  }, [users, loggedInUser]);

  const [toBeEditedUser, setToBeEditedUser] = useState<TUser>();
  const [isAddUsersModalOpen, setIsAddUsersModalOpen] = useState(false);
  const [isEditUsersModalOpen, setIsEditUsersModalOpen] = useState(false);

  // const getAddUserAmountString = () => {
  //   if (users.length >= MAX_USERS) {
  //     return strings.usersPage.reachedMaxUser;
  //   }

  //   const userAmtLeft = MAX_USERS - users.length;
  //   return userAmtLeft !== 1
  //     ? strings.usersPage.addMoreUserDescription(userAmtLeft)
  //     : strings.usersPage.addUserDescription;
  // };

  const onToggleNotifications = async (user: TUser) => {
    // Replace with your function, e.g. handleCheckboxToggle(user)
    user.send_device_notifications = !user.send_device_notifications;
    // Call your API to update the user
    // apiPatch(`/users/${user._id}`, { send_device_notifications: user.send_device_notifications });
    console.log("update user", user);
    await updateUser(user._id, user);
    await mutate(); // Refresh the user list after update
    toast.success(
      `Notifications for ${user.first_name} have been ${
        user.send_device_notifications ? "enabled" : "disabled"
      }`
    );
  };

  const onEditUser = (user: TUser) => {
    setToBeEditedUser(user);
    setIsEditUsersModalOpen(true);
  };

  return (
    <>
      <div className="p-0 mb-20">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-white">
                <th
                  className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-500`}
                  // onClick={() => sortUsers(true)}
                >
                  {strings.common.user}
                  {/* <span>
                    {sortingRules.byName.isAscending ? (
                      <ArrowDownNarrowWide className="inline w-4 h-4 ml-1 cursor-pointer" />
                    ) : (
                      <ArrowUpNarrowWide className="inline w-4 h-4 ml-1 cursor-pointer" />
                    )}
                  </span> */}
                </th>
                <th
                  className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-500 max-w-60 hidden md:table-cell`}
                  // onClick={() => sortUsers(true)}
                >
                  {strings.common.email}
                  {/* <span>
                    {sortingRules.byEmail.isAscending ? (
                      <ArrowDownNarrowWide className="inline w-4 h-4 ml-1 cursor-pointer" />
                    ) : (
                      <ArrowUpNarrowWide className="inline w-4 h-4 ml-1 cursor-pointer" />
                    )}
                  </span> */}
                </th>
                <th
                  className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-500 max-w-60 hidden md:table-cell`}
                >
                  Notifications
                </th>
                {isAdmin(loggedInUser) && <th className="text-right">&nbsp;</th>}
              </tr>
            </thead>
            <tbody>
              {sortedUsers?.map((user) => (
                <tr 
                  key={user._id} 
                  className={`border-white hover:bg-gray-100 ${
                    user._id === loggedInUser?.publicMetadata["external_id"] 
                      ? "bg-blue-50 hover:bg-blue-100" 
                      : ""
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    <div className="flex flex-col">
                      <div>
                        {user.invite_pending ? (
                          <em className="text-zinc-400">
                            {strings.usersPage.invitePending}
                          </em>
                        ) : (
                          user.first_name
                        )}
                      </div>
                      <div className="md:hidden text-xs text-gray-500 mt-1">
                        {user.email_address}
                      </div>
                    </div>
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-900 overflow-hidden text-ellipsis md:max-w-full max-w-48 hidden md:table-cell`}
                  >
                    {user.email_address}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-xs font-medium overflow-hidden text-ellipsis md:max-w-full max-w-48 hidden md:table-cell`}
                  >
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.send_device_notifications 
                        ? "bg-green-100 text-green-800" 
                        : "bg-gray-100 text-gray-800"
                    }`}>
                      {user.send_device_notifications ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold">
                    {(isAdmin(loggedInUser) || user._id === loggedInUser?.publicMetadata["external_id"]) && (
                      <div className="flex justify-end">
                        <button
                          className="text-blue-700 hover:text-blue-900 flex items-center gap-2"
                          onClick={() => onEditUser(user)}
                        >
                          <Edit3 className="w-4 h-4" />
                          <span className="hidden md:inline-block">
                            {user._id === loggedInUser?.publicMetadata["external_id"] ? "Edit" : "Edit Settings"}
                          </span>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ActionBar>
          {/* <p className="text-sm text-gray-500">
            {getAddUserAmountString() as string}
          </p> */}
          <button
            onClick={() => setIsAddUsersModalOpen(true)}
            // disabled={users.length >= MAX_USERS}
            className={`ml-auto px-4 py-3 border border-transparent rounded-md shadow-sm text-xs font-bold uppercase tracking-wider text-white ${
              // users.length >= 5
              // ? "bg-gray-300 cursor-not-allowed"
              // :
              "bg-blue-700 hover:bg-blue-800"
            }`}
          >
            {strings.usersPage.addNewUser}
          </button>
        </ActionBar>
      </div>
      {isAddUsersModalOpen && (
        <UserModal onClose={() => setIsAddUsersModalOpen(false)} />
      )}
      {isEditUsersModalOpen && (
        <EditUserModal
          onClose={() => setIsEditUsersModalOpen(false)}
          user={toBeEditedUser}
          isCurrentUser={toBeEditedUser?._id === loggedInUser?.publicMetadata["external_id"]}
        />
      )}
    </>
  );
};

export default UsersPage;
